import type {
  FormAnswer,
  FormNotice,
  Message,
  MessageId,
  MessageStatus,
} from '../fixtures/domain.js';
import { Icon } from '../components/Icon.js';
import { MessageMarkdown } from '../components/MessageMarkdown.js';
import { AttachmentView } from './AttachmentView.js';
import { formatClock } from '../util/format.js';
import { MessageCtaSlot } from './MessageCtaButtons.js';
import { MessageQuote, type QuotedPreview } from './MessageQuote.js';
import { ctaOf } from '../data/cta.js';
import { formOf } from '../data/forms.js';
import { FormAnswersCard, FormInviteCard } from '../form/FormMessageCard.js';
import { useResponseLink } from '../form/responseLink.js';
import { JobMessageCard } from './JobMessageCard.js';
import styles from './MessageBubble.module.css';

interface MessageBubbleProps {
  message: Message;
  own: boolean;
  /** Last of a run by one sender: only this bubble shows the time and receipt. */
  endsGroup: boolean;
  /** The session user, for the CTA clickability rule. */
  currentUserId: string;
  /** Sends a reply-mode CTA's text. Handed to every bubble, since any of them
   *  may carry one. */
  sendReply: (text: string, quotedMessageId: MessageId, plain?: boolean) => Promise<void>;
  /** The message this one answers, already resolved by the thread. */
  quoted?: QuotedPreview;
  /** For an invite card: the answers its seal points at, resolved by the thread. */
  formAnswers?: FormAnswer[];
  /** For an answers card: the notice that asked, resolved by the thread. */
  formNotice?: FormNotice;
  onJumpToMessage: (id: MessageId) => void;
  /** Absent when this message cannot be replied to — an ended chat, or a system
   *  note nobody wrote. */
  onReplyTo?: (message: Message) => void;
  /** Absent on our own messages and wherever there is no one to report. */
  onReport?: (message: Message) => void;
  /** Absent unless this message is still ours to edit. */
  onEdit?: (message: Message) => void;
}

export function MessageBubble({
  message,
  own,
  endsGroup,
  currentUserId,
  sendReply,
  quoted,
  formAnswers,
  formNotice,
  onJumpToMessage,
  onReplyTo,
  onReport,
  onEdit,
}: MessageBubbleProps) {
  const responseLink = useResponseLink();
  const cta = ctaOf(message);
  const form = formOf(message);
  const buttons = cta && (
    <MessageCtaSlot message={message} currentUserId={currentUserId} sendReply={sendReply} />
  );

  if (message.system) {
    // A system note is nobody's message, so it stays centred. With buttons it
    // has to become a card: the note's pill shape has no room for a stack, and
    // full-width rows need a straight edge to run to.
    if (!cta) {
      return <div className={styles.system}>{message.text}</div>;
    }
    return (
      <div className={styles.systemCard}>
        <div className={styles.systemCardText}>{message.text}</div>
        {buttons}
      </div>
    );
  }

  const side = own ? styles.own : styles.their;

  return (
    <div className={endsGroup ? side : `${side} ${styles.grouped}`}>
      <div className={styles.bubbleRow}>
        <div
          className={[
            styles.bubble,
            // A form or job card shares the CTA's full-bleed foot treatment.
            cta || form || message.formResponse || message.job ? styles.hasBlock : '',
            // Sized by the attachment rather than by the caption. See
            // `.hasAttachment` for why the bubble has to be told.
            message.attachments?.length ? styles.hasAttachment : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {quoted && (
            <div className={styles.quoteSlot}>
              <MessageQuote quoted={quoted} onJump={onJumpToMessage} />
            </div>
          )}
          {/* Inside the bubble, not in the meta row below it: that row renders
              only on the last bubble of a run, so a greeting the employer then
              followed up on would lose its label exactly where the distinction
              matters most. */}
          {message.autoReply && (
            <div className={styles.autoReplyTag}>
              <Icon name="bolt" size={12} />
              Automatic reply
            </div>
          )}
          {message.attachments?.map((a, i) => (
            <AttachmentView
              key={`${message.id}-att-${i}`}
              attachment={a}
              channelId={message.channelId}
            />
          ))}
          {/* Not on an answer: its text is the fallback for surfaces that do
              not draw cards, and the card below says it with the answers. */}
          {message.text !== '' &&
            !message.formResponse &&
            !(message.job && message.text === message.job.title) && (
              <div className={styles.bubbleText}>
                <MessageMarkdown text={message.text} />
              </div>
            )}
          {form && (
            <FormInviteCard
              notice={form}
              answers={formAnswers}
              link={
                responseLink?.opens(form.formId)
                  ? { open: () => responseLink.openForm(form.formId) }
                  : undefined
              }
            />
          )}
          {message.job && <JobMessageCard job={message.job} />}
          {message.formResponse && (
            <FormAnswersCard
              response={message.formResponse}
              notice={formNotice}
              fallback={message.text}
              onView={
                responseLink?.opens(message.formResponse.formId)
                  ? () => responseLink.open(message.formResponse!.formId, message.formResponse!.id)
                  : undefined
              }
            />
          )}
          {buttons}
        </div>
        {/* Sits outside the bubble, on the side away from the speaker, so it
            never covers the words. Hidden until the row is hovered or the button
            itself is focused — it is an action on a message, not part of the
            message, and one visible per bubble would turn a thread into a
            control panel. */}
        {onReplyTo && (
          <button
            type="button"
            className={styles.messageAction}
            onClick={() => onReplyTo(message)}
            title="Reply"
            aria-label="Reply to this message"
          >
            <Icon name="reply" size={15} />
          </button>
        )}
        {onReport && (
          <button
            type="button"
            className={styles.reportAction}
            onClick={() => onReport(message)}
            title="Report"
            aria-label="Report this message"
          >
            <Icon name="flag" size={14} />
          </button>
        )}
        {onEdit && (
          <button
            type="button"
            className={styles.messageAction}
            onClick={() => onEdit(message)}
            title="Edit"
            aria-label="Edit this message"
          >
            <Icon name="pencil" size={15} />
          </button>
        )}
      </div>
      {/* Every edited bubble says so, not just the last of a run that carries the time. */}
      {(endsGroup || message.editedAt) && (
        <span className={styles.meta}>
          {message.editedAt && <span>(edited)</span>}
          {endsGroup && formatClock(message.createdAt)}
          {endsGroup && own && <ReadReceipt status={message.status} />}
        </span>
      )}
    </div>
  );
}

/**
 * How far the candidate has got with this message. Own messages only: a receipt
 * on theirs would tell them what they already know.
 *
 * On a closing message the read is the highest-stakes signal in the app, since
 * it starts the 30-minute deletion fade. The tick and the header countdown come
 * from the same read but not the same place: the tick rides the Stream socket
 * while the countdown waits on the backend stamping the channel, so the tick
 * lands first.
 */
function ReadReceipt({ status }: { status: MessageStatus }) {
  // Still in flight, so there is nothing true to claim yet.
  if (status === 'sending') return null;

  const read = status === 'read';
  const reached = read || status === 'delivered';
  return (
    <span
      className={read ? styles.receiptRead : styles.receipt}
      title={read ? 'Read' : reached ? 'Delivered' : 'Sent'}
    >
      <Icon name={reached ? 'checkCheck' : 'check'} size={14} />
    </span>
  );
}
